/**
 * FLOTTENWEIT IDENTISCH — nicht pro App editieren (Regel #24).
 * Quelle: 121apps-foundation/packages/ui/seoKopf.ts
 *
 * Wie Crawler die Sprachfassungen einer öffentlichen Seite finden — EINE Regel
 * für alle Leser (docs/NEXTJS_MIGRATION.md, Erkenntnis 18; ROLLOUT „Von der
 * deutschen Seite zu allen Sprachen“):
 *
 * - `seoKopf`        canonical, hreflang (mit `x-default`) und ob die Fassung in
 *                    den Index gehört. Die Server-Seite (`generateMetadata`),
 *                    die Sitemap und die Browser-Komponente (`HreflangTags`)
 *                    rufen dieselbe Funktion — eine zweite Fassung hiesse: der
 *                    Crawler liest andere Alternativen als der Browser.
 * - `sprachFassungen` dieselben Fassungen als Liste für den Sprachumschalter
 *                    aus echten `<a href>` (KI-Crawler werten weder hreflang
 *                    noch Sitemap-Alternativen verlässlich aus, sie folgen
 *                    Links).
 * - `startOrt`       wohin `/` einen Besucher ohne Präfix schickt: Cookie →
 *                    `Accept-Language` → Vorgabe der Konfiguration. Nie die
 *                    IP — eine Weiterleitung nach Standort sähe ein Crawler
 *                    aus den USA nur auf Englisch.
 *
 * Rein: Alles kommt über die Parameter (die Matrix aus
 * `/api/i18n/locale-config`), nichts aus dem Modul-Zustand von
 * `localeConfig.ts` — auf dem Server laufen Anfragen verschiedener Sprachen
 * gleichzeitig.
 *
 * Entstanden in 121surveys (`src/i18n/seoKopf.ts`, 04.10.2026), erweitert um
 * Sprachlinks und Startseite für 121suite (14.10.2026).
 */
import type { LocaleConfig } from './localeConfig'

export interface SeoKopf {
  /** `hreflang` → absolute Adresse, inklusive `x-default`. */
  alternativen: Record<string, string>
  canonical: string
  indexierbar: boolean
}

export interface Ort {
  land: string
  sprache: string
}

/**
 * Ziel von `x-default` — flottenweit fest, NICHT die Vorgabe der App.
 *
 * Die Vorgabe der Matrix (`defaultCountry`/`defaultLanguage`) beantwortet eine
 * andere Frage: wohin ein Besucher ohne Sprachwunsch geht. 121vendors ist
 * deutsch-zuerst (Vorgabe `de/de`), sein `x-default` zeigt trotzdem auf die
 * englische Fassung (Kanon ROLLOUT „Von der deutschen Seite zu allen
 * Sprachen“, Punkt 3; der Wächter `check_seo_html.py` prüft genau das).
 */
export const X_DEFAULT: Ort = { land: 'gb', sprache: 'en' }

function praefix(config: LocaleConfig, sprache: string): string {
  return config.languages[sprache]?.urlPrefix || sprache
}

/**
 * Die Regel:
 * - Jede Fassung mit `seoEnabled` ist eine Alternative.
 * - `x-default` ist `X_DEFAULT` (gb/english) — unabhängig von der Vorgabe der
 *   App. Fehlt diese Fassung unter den SEO-Fassungen, wirft die Funktion:
 *   ein `x-default` auf eine Seite ausserhalb des Index wäre still falsch.
 * - Eine Fassung ohne `seoEnabled` gehört nicht in den Index und zeigt per
 *   canonical auf die erste SEO-Sprache ihres Landes, sonst auf `x-default`.
 *
 * @param basis  Ursprung ohne Schrägstrich am Ende (`https://www.121suite.com`)
 * @param rest   Pfad HINTER dem Sprach-Präfix (`/pricing`); `''` für die Wurzel
 */
export function seoKopf(
  config: LocaleConfig,
  basis: string,
  land: string,
  sprache: string,
  rest: string,
): SeoKopf {
  const adresse = (l: string, p: string) => `${basis}/${l}/${p}${rest}`
  const alle = config.hreflangAlternates || []
  const ziel = alle.find((a) => a.country === X_DEFAULT.land && a.language === X_DEFAULT.sprache)
  if (!ziel?.seoEnabled) {
    throw new Error(
      `seoKopf: x-default braucht die SEO-Fassung ${X_DEFAULT.land}/${X_DEFAULT.sprache} — ` +
        'sie fehlt in hreflangAlternates oder hat seoEnabled=false',
    )
  }
  const xDefault = adresse(ziel.country, ziel.urlPrefix)

  const alternativen: Record<string, string> = {}
  for (const a of alle) {
    if (a.seoEnabled) alternativen[a.hreflang] = adresse(a.country, a.urlPrefix)
  }
  alternativen['x-default'] = xDefault

  const eigene = alle.find((a) => a.country === land && a.language === sprache)
  if (eigene?.seoEnabled) {
    return { alternativen, canonical: adresse(land, eigene.urlPrefix), indexierbar: true }
  }
  const ersatz = config.countries[land]?.seoLanguages?.[0]
  return {
    alternativen,
    canonical: ersatz ? adresse(land, praefix(config, ersatz)) : xDefault,
    indexierbar: false,
  }
}

export interface SprachFassung extends Ort {
  /** BCP-47, wie im hreflang (`de-CH`). */
  hreflang: string
  /** Pfad mit Präfix (`/ch/deutsch/pricing`) — relativ, für `<a href>`. */
  pfad: string
  /** Name des Landes in seiner Sprache (`Schweiz`). */
  landName: string
  /** Name der Sprache in ihr selbst (`Deutsch`). */
  sprachName: string
  flagge: string
}

/**
 * Jede indexierbare Fassung der Seite `rest`, nach Sprache und Land sortiert —
 * die Ziele des Sprachumschalters. Die eigene Fassung steht mit in der Liste
 * (der Umschalter markiert sie), damit alle Seiten dieselbe Liste tragen.
 */
export function sprachFassungen(config: LocaleConfig, rest: string): SprachFassung[] {
  const liste: SprachFassung[] = []
  for (const a of config.hreflangAlternates || []) {
    if (!a.seoEnabled) continue
    const land = config.countries[a.country]
    const sprache = config.languages[a.language]
    if (!land || !sprache) continue
    liste.push({
      land: a.country,
      sprache: a.language,
      hreflang: a.hreflang,
      pfad: `/${a.country}/${a.urlPrefix}${rest}`,
      landName: land.nameNative,
      sprachName: sprache.nameNative,
      flagge: land.flag,
    })
  }
  return liste.sort(
    (x, y) => x.sprachName.localeCompare(y.sprachName) || x.landName.localeCompare(y.landName),
  )
}

/** Das Heimatland einer Sprache — nur, wenn es die Sprache auch führt. */
function heimatland(config: LocaleConfig, sprache: string): string | null {
  const eigenes = config.languages[sprache]?.defaultCountry?.toLowerCase()
  if (eigenes && config.countries[eigenes]?.languages.includes(sprache)) return eigenes
  if (config.countries[config.defaultCountry]?.languages.includes(sprache)) return config.defaultCountry
  const erstes = Object.values(config.countries)
    .filter((c) => c.languages.includes(sprache))
    .map((c) => c.code)
    .sort()[0]
  return erstes ?? null
}

/** `de-CH,de;q=0.9,en;q=0.5` → `[['de','ch'], ['de',null], ['en',null]]`, nach Gewicht. */
function sprachWuensche(kopf: string): Array<[string, string | null]> {
  return kopf
    .split(',')
    .map((teil, i) => {
      const [tag, ...parameter] = teil.trim().split(';')
      const q = parameter.map((p) => p.trim()).find((p) => p.startsWith('q='))
      const gewicht = q ? Number(q.slice(2)) : 1
      const [sprache, region] = tag.trim().toLowerCase().split('-')
      return { sprache, region: region ?? null, gewicht: Number.isFinite(gewicht) ? gewicht : 0, i }
    })
    .filter((w) => w.sprache && w.sprache !== '*' && w.gewicht > 0)
    .sort((a, b) => b.gewicht - a.gewicht || a.i - b.i)
    .map((w) => [w.sprache, w.region])
}

/**
 * Der Ort für eine Sprache: das gewünschte Land, wenn es die Sprache führt,
 * sonst ihr Heimatland. `null`, wenn die Matrix die Sprache nicht kennt oder
 * kein Land sie führt.
 *
 * Auch das Ziel des Sprachumschalters: Wer auf `/de/deutsch/…` „English“
 * wählt, landet auf `/gb/english/…` — Deutschland führt kein Englisch, und
 * `/de/english/…` wäre eine ungültige Adresse (14.10.2026, 121vendors: die
 * Umleitung danach verdoppelte das Präfix zu `/de/deutsch/de/english/…`).
 */
export function ortFuer(config: LocaleConfig, sprache: string, land: string | null): Ort | null {
  if (!config.languages[sprache]) return null
  if (land && config.countries[land]?.languages.includes(sprache)) return { land, sprache }
  const heim = heimatland(config, sprache)
  return heim ? { land: heim, sprache } : null
}

/**
 * Wohin ein Besucher ohne Sprach-Präfix gehört (Startseite `/`).
 *
 * 1. Cookie `locale` (+ `country`) — die eigene Wahl eines Wiederkehrers.
 * 2. `Accept-Language` — in der Reihenfolge der Gewichte; das Land aus der
 *    Region, wenn es die Sprache führt (`de-CH` → `ch`), sonst ihr Heimatland.
 * 3. Die Vorgabe der Konfiguration (`gb`/`en`) — ein Crawler schickt meist
 *    weder Cookie noch `Accept-Language`.
 */
export function startOrt(
  config: LocaleConfig,
  cookie: { locale?: string | null; country?: string | null },
  acceptLanguage: string | null,
): Ort {
  const ausCookie = cookie.locale
    ? ortFuer(config, cookie.locale.toLowerCase(), cookie.country?.toLowerCase() ?? null)
    : null
  if (ausCookie) return ausCookie
  for (const [sprache, region] of sprachWuensche(acceptLanguage ?? '')) {
    const ort = ortFuer(config, sprache, region)
    if (ort) return ort
  }
  return { land: config.defaultCountry, sprache: config.defaultLanguage }
}

/** Präfix-Pfad eines Orts: `{land:'ch', sprache:'de'}` + `/pricing` → `/ch/deutsch/pricing`. */
export function ortPfad(config: LocaleConfig, ort: Ort, rest: string): string {
  return `/${ort.land}/${praefix(config, ort.sprache)}${rest}`
}

/**
 * Fassungen nach Sprache gruppiert — die Reihenfolge von `sprachFassungen` bleibt.
 *
 * Liegt hier und nicht in `SprachLinks.tsx`: Eine .tsx-Datei, die neben der
 * Komponente eine Funktion exportiert, löst in jeder Vite/ESLint-App
 * `react-refresh/only-export-components` aus (121suite musste die Regel dafür
 * abschalten).
 */
export function nachSprache(fassungen: SprachFassung[]): Array<{ sprache: string; name: string; fassungen: SprachFassung[] }> {
  const gruppen: Array<{ sprache: string; name: string; fassungen: SprachFassung[] }> = []
  for (const f of fassungen) {
    let gruppe = gruppen.find((g) => g.sprache === f.sprache)
    if (!gruppe) {
      gruppe = { sprache: f.sprache, name: f.sprachName, fassungen: [] }
      gruppen.push(gruppe)
    }
    gruppe.fassungen.push(f)
  }
  return gruppen
}
